import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Space, Modal, Form, Input, Select, message, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CarOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const ParkingManagementPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState<any>(null);
    const [form] = Form.useForm();

    // Fetch data (Tạm thời dùng mock, sau này sẽ có API list cards)
    const fetchData = async () => {
        setLoading(true);
        try {
            // TODO: Tạo API GET /api/v1/parking/admin/cards
            // const response = await axios.get('/api/v1/parking/admin/cards');
            // setData(response.data.data);

            // Temporary mock data
            setData([
                { Id: 1, StudentId: '1001', CardCode: 'CARD001', VehicleTypeId: 1, LicensePlate: '29A-123.45', CurrentBalance: 50000, IsActive: true, CreatedAt: '2024-01-20' },
                { Id: 2, StudentId: '1002', CardCode: 'CARD002', VehicleTypeId: 2, LicensePlate: '29B-567.89', CurrentBalance: 12000, IsActive: true, CreatedAt: '2024-01-21' },
            ]);
        } catch (error) {
            message.error('Không thể tải danh sách thẻ xe');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenModal = (card?: any) => {
        if (card) {
            setEditingCard(card);
            form.setFieldsValue(card);
        } else {
            setEditingCard(null);
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (editingCard) {
                // TODO: Tạo API PUT /api/v1/parking/admin/cards/:id
                message.warning('Chức năng cập nhật đang phát triển');
            } else {
                // Đăng ký thẻ mới - Sử dụng API thực
                const vehicleTypeMap: any = {
                    1: 'Motor',
                    2: 'Bicycle',
                    3: 'Car'
                };

                await axios.post('/api/v1/parking/admin/register-card', {
                    studentId: values.StudentId,
                    vehicleType: vehicleTypeMap[values.VehicleTypeId] || 'Motor',
                    licensePlate: values.LicensePlate,
                    initialBalance: 0
                });

                message.success('Đăng ký thẻ thành công');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error: any) {
            console.error('Lỗi lưu dữ liệu:', error);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Không thể lưu dữ liệu';
            message.error(errorMsg);
        }
    };

    const vehicleTypes: any = { 1: 'Xe máy', 2: 'Xe máy điện', 3: 'Ô tô' };

    const columns = [
        { title: 'MSSV', dataIndex: 'StudentId', key: 'StudentId' },
        { title: 'Mã thẻ', dataIndex: 'CardCode', key: 'CardCode' },
        {
            title: 'Loại xe',
            dataIndex: 'VehicleTypeId',
            key: 'VehicleTypeId',
            render: (type: number) => vehicleTypes[type] || 'Khác'
        },
        { title: 'Biển số', dataIndex: 'LicensePlate', key: 'LicensePlate' },
        {
            title: 'Số dư',
            dataIndex: 'CurrentBalance',
            key: 'CurrentBalance',
            render: (val: number) => (
                <Text type={val < 10000 ? 'danger' : 'success'}>
                    {val.toLocaleString('vi-VN')} đ
                </Text>
            )
        },
        {
            title: 'Trạng thái',
            dataIndex: 'IsActive',
            key: 'IsActive',
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>{active ? 'Đang hoạt động' : 'Đã khóa'}</Tag>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_: any, record: any) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
                    <Button icon={<DeleteOutlined />} danger />
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card title={
                <Space>
                    <CarOutlined />
                    <span>Quản lý đăng ký thẻ xe</span>
                </Space>
            } extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                    Đăng ký mới
                </Button>
            }>
                <div style={{ marginBottom: 16 }}>
                    <Space>
                        <Input placeholder="Tìm kiếm MSSV/Thẻ/Biển số" prefix={<SearchOutlined />} style={{ width: 300 }} />
                        <Button type="default" icon={<SearchOutlined />}>Tìm kiếm</Button>
                    </Space>
                </div>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="Id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editingCard ? 'Chỉnh sửa thẻ xe' : 'Đăng ký thẻ xe mới'}
                open={isModalOpen}
                onOk={handleSave}
                onCancel={() => setIsModalOpen(false)}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="StudentId" label="Mã sinh viên" rules={[{ required: true, message: 'Vui lòng nhập MSSV' }]}>
                        <Input placeholder="Nhập MSSV" />
                    </Form.Item>
                    <Form.Item name="VehicleTypeId" label="Loại xe" initialValue={1}>
                        <Select>
                            <Option value={1}>Xe máy</Option>
                            <Option value={2}>Xe máy điện</Option>
                            <Option value={3}>Ô tô</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="LicensePlate" label="Biển số xe">
                        <Input placeholder="VD: 29A-123.45" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ParkingManagementPage;
